import React, { useState, useRef } from "react";

import { Button, Col, Empty, Row, Tabs } from "antd";
import { blue, cyan } from "@ant-design/colors";
import { Bar, Line } from "@ant-design/charts";
import { chain, sumBy, isEmpty } from "lodash";

import Moment from "moment";
import { extendMoment } from "moment-range";

import { convertInchesToCm, dataOffsetDate } from "../../libs/utils";

const moment = extendMoment(Moment);
const MILLISECONDS_IN_MIN = 60000;

const { TabPane } = Tabs;

const filterData = [
  {
    key: 1,
    title: "Last 24 hours",
    value: 1,
    date: moment().subtract(1, "days"),
  },
  {
    key: 2,
    title: "Last 7 days",
    value: 7,
    date: moment().subtract(7, "days"),
  },
  {
    key: 3,
    title: "Last 30 days",
    value: 30,
    date: moment().subtract(30, "days"),
  },
];

const activeButtonStyle = {
  color: "#40a9ff",
  borderColor: "#40a9ff",
};

const RobotPerformance = (props) => {
  const [filterButtonKey, setFilterButtonKey] = useState(3); // default view as last 30 days

  const ref = useRef();

  // TODO: this can be refactored into map function however it does not render with map currently
  const operations = (
    <>
      <Button
        onClick={() => setFilterButtonKey(1)}
        style={filterButtonKey === 1 ? activeButtonStyle : {}}
      >
        {filterData.find((item) => item.key === 1).title}
      </Button>
      <Button
        onClick={() => setFilterButtonKey(2)}
        style={filterButtonKey === 2 ? activeButtonStyle : {}}
      >
        {filterData.find((item) => item.key === 2).title}
      </Button>
      <Button
        onClick={() => setFilterButtonKey(3)}
        style={filterButtonKey === 3 ? activeButtonStyle : {}}
      >
        {filterData.find((item) => item.key === 3).title}
      </Button>
    </>
  );

  const today = moment().format("YYYY-MM-DD");
  const start = moment(today).subtract(1, "month").format("YYYY-MM-DD");

  const range = moment.range(start, today);

  const {
    robot: {
      summary: {
        date,
        data: [
          ,
          weldHours = {
            // count,
            // hours,
            // total_counts,
            // total_hours,
            // weld_time_per_day_in_month,
            // weld_time_per_operator_name,
            // weld_time_per_welder_id_ref
          },
          weldInches = {
            // total_weld_inches,
            // weld_inches,
            // weld_inches_per_operator_name,
            // weld_inches_per_welder_id,
            // weld_inches_per_day_in_month,
          },
          // weldPipe = {},
        ],
      },
    },
    weldRun,
    showOnlyPane,
  } = props;

  const isMetric =
    weldRun && weldRun[0] && weldRun[0].units_system === "metric";

  // Constructing Performance data with weld run table
  const weldData = chain(weldRun)
    .groupBy((item) => {
      return moment(item.arc_on_utc).format("YYYY-MM-DD");
    })
    .map((item, key) => {
      const inches =
        sumBy(item, (i) => {
          if (
            moment.duration({ ...i.weld_time }).asMilliseconds() >=
            MILLISECONDS_IN_MIN
          ) {
            return i.weld_inches;
          }
        }) || 0;

      const hours = sumBy(item, (i) => {
        return moment.duration({ ...i.weld_time });
      });
      return {
        date: key,
        inches: isMetric ? convertInchesToCm(inches).toFixed(1) : inches,
        hours: hours,
      };
    })
    .filter((item) => {
      return moment(item.date).isSameOrAfter(
        filterData
          .find((i) => i.key === filterButtonKey)
          .date.format("YYYY-MM-DD")
      );
    })
    .value();

  const data = Array.from(range.by("day"))
    .map((item) => {
      const date = item.format("YYYY-MM-DD");
      const weldPerDay = weldData.find((item) => item.date === date) || {};

      return {
        date: date,
        inches: weldPerDay.inches || 0,
        hours: weldPerDay.hours || 0,
      };
    })
    .filter((item) => {
      return moment(item.date).isSameOrAfter(
        filterData
          .find((i) => i.key === filterButtonKey)
          .date.format("YYYY-MM-DD")
      );
    });

  // Constructing Performance data with weld information in NovHealth of S3
  // TODO: Once DB Synced is fully reliable, this should be removed
  const s3Data = Array.from(range.by("day"))
    .map((item) => {
      const weldInchesData =
        weldInches.weld_inches_per_day_in_month &&
        weldInches.weld_inches_per_day_in_month.filter((i) => {
          return i.date === item.format("YYYY-MM-DD");
        });
      const weldHoursData =
        weldHours.weld_time_per_day_in_month &&
        weldHours.weld_time_per_day_in_month.filter((i) => {
          return i.date === item.format("YYYY-MM-DD");
        });
      return {
        date: item.format("YYYY-MM-DD"),
        inches:
          (weldInchesData &&
            weldInchesData.length > 0 &&
            weldInchesData[0].sum) ||
          0,
        hours:
          (weldHoursData &&
            weldHoursData.length > 0 &&
            moment.duration(weldHoursData[0].sum)) ||
          0,
      };
    })
    .filter((item) => {
      return moment(item.date).isSameOrAfter(
        filterData
          .find((i) => i.key === filterButtonKey)
          .date.format("YYYY-MM-DD")
      );
    });

  const weldHoursSum = sumBy(data, (i) => {
    return moment.duration({ milliseconds: i.hours });
  });
  const weldInchesSum = sumBy(data, (i) => {
    return i.inches;
  });

  const s3WeldHoursSum = sumBy(s3Data, (item) => item.hours);
  const s3WeldInchesSum = sumBy(s3Data, (item) => item.inches);

  const weldInchesConfig = {
    data: weldInchesSum ? data : s3Data,
    xField: "date",
    yField: "inches",
    xAxis: {
      label: {
        formatter: function formatter(v) {
          return moment(v).format("MMM-DD");
        },
      },
    },
    yAxis: {
      label: {
        formatter: function formatter(v) {
          return v + (isMetric ? "cm" : "″");
        },
      },
    },
    tooltip: {
      formatter: (datum) => {
        return {
          name: datum.date,
          value: datum.inches + (isMetric ? "cm" : "″"),
        };
      },
    },
  };
  const weldHoursConfig = {
    data: weldHoursSum ? data : s3Data,
    xField: "date",
    yField: "hours",
    xAxis: {
      label: {
        formatter: function formatter(v) {
          return moment(v).format("MMM-DD");
        },
      },
    },
    yAxis: {
      label: {
        formatter: function formatter(v) {
          return moment.duration(v).asMinutes().toFixed(0) + " min";
        },
      },
    },
    tooltip: {
      formatter: (datum) => {
        return {
          name: datum.date,
          value: moment.duration(datum.hours).asMinutes().toFixed(1) + " min",
        };
      },
    },
  };

  const dbOperatorWeldData = chain(weldRun)
    .filter((item) => {
      return moment(item.arc_on_utc).isSameOrAfter(
        filterData
          .find((i) => i.key === filterButtonKey)
          .date.format("YYYY-MM-DD")
      );
    })
    .groupBy((item) => {
      return item.operator_name;
    })
    .map((item, key) => {
      const inches =
        sumBy(item, (i) => {
          if (
            moment.duration({ ...i.weld_time }).asMilliseconds() >=
            MILLISECONDS_IN_MIN
          ) {
            return i.weld_inches;
          }
        }) || 0;

      const hours = sumBy(item, (i) => {
        return moment.duration({ ...i.weld_time });
      });
      return { name: key, inches: inches, hours: hours };
    })
    .value();

  const s3OperatorWeldHours =
    weldHours &&
    weldHours.weld_time_per_operator_name &&
    weldHours.weld_time_per_operator_name
      .map((item) => {
        return { name: item.operator_name, hours: moment.duration(item.sum) };
      })
      .sort((a, b) => {
        if (a.hours < b.hours) {
          return 1;
        }
        if (a.hours > b.hours) {
          return -1;
        }
        return 0;
      });

  const s3OperatorWeldInches =
    weldInches &&
    weldInches.weld_inches_per_operator_name &&
    weldInches.weld_inches_per_operator_name.map((item) => {
      return { name: item.operator_name, inches: item.sum };
    });

  const operatorsWeldHours = !isEmpty(weldRun)
    ? !isEmpty(dbOperatorWeldData) && dbOperatorWeldData
    : !isEmpty(s3OperatorWeldHours) && s3OperatorWeldHours;

  const operatorsWeldInches = !isEmpty(weldRun)
    ? !isEmpty(dbOperatorWeldData) && dbOperatorWeldData
    : !isEmpty(s3OperatorWeldInches) && s3OperatorWeldInches;

  const dataOperatorsWeldInches = operatorsWeldInches
    ? [
        ...operatorsWeldInches,
        {
          name: "Overall",
          inches: operatorsWeldInches
            ? operatorsWeldInches.reduce((prev, curr) => prev + curr.inches, 0)
            : 0,
        },
      ]
    : null;

  const dataOperatorsWeldHours = operatorsWeldHours
    ? [
        ...operatorsWeldHours,
        {
          name: "Overall",
          hours: operatorsWeldHours
            ? operatorsWeldHours.reduce((prev, curr) => prev + curr.hours, 0)
            : 0,
        },
      ]
    : null;

  // ##################################################
  // Bar chart config for top operators weld hours
  // ##################################################
  const operatorsWeldHoursConfig = {
    data: dataOperatorsWeldHours,
    xField: "hours",
    yField: "name",
    seriesField: "name",
    color: ({ name }) => {
      return name === "Overall"
        ? blue[7] //#0050b3
        : blue[5]; //#1890ff
    },
    legend: false,
    height: 150,
    barWidthRatio: 0.9,
    maxBarWidth: 20,
    xAxis: {
      label: {
        formatter: function formatter(v) {
          return moment.duration(v).asHours().toFixed(1) + "hr";
        },
      },
    },
    tooltip: {
      formatter: (datum) => {
        return {
          name: datum.name,
          value:
            datum.hours &&
            moment.duration(datum.hours).asHours().toFixed(1) + " hours",
        };
      },
    },
  };

  // ##################################################
  // Bar chart config for top operators weld inches
  // ##################################################
  const operatorsWeldInchesConfig = {
    data: dataOperatorsWeldInches,
    xField: "inches",
    yField: "name",
    seriesField: "name",
    color: ({ name }) => {
      return name === "Overall"
        ? cyan[7] //#006d75
        : cyan[5]; //#13c2c2
    },
    legend: false,
    height: 150,
    barWidthRatio: 0.9,
    maxBarWidth: 20,
    xAxis: {
      label: {
        formatter: function formatter(v) {
          return v + (isMetric ? " cm" : " ″");
        },
      },
    },
    tooltip: {
      formatter: (datum) => {
        return {
          name: datum.name,
          value:
            datum.inches &&
            datum.inches.toFixed(1) + (isMetric ? " cm" : " inches"),
        };
      },
    },
  };

  return (
    <>
      <Tabs
        tabBarExtraContent={operations}
        onTabClick={(key) => {
          // Performance per Operator is only for last 7 days
          if (key === 3) setFilterButtonKey(2);
        }}
        defaultActiveKey={showOnlyPane}
      >
        {(!showOnlyPane || showOnlyPane === "1") && (
          <TabPane tab={"Weld Hours"} key="1" style={{ height: 407 }}>
            {weldHoursSum || s3WeldHoursSum ? (
              <Line {...weldHoursConfig} chartRef={ref} />
            ) : (
              <Empty
                description={
                  weldHoursSum || s3WeldHoursSum
                    ? `No Weld Data`
                    : `No Weld Data Since ${dataOffsetDate(
                        date,
                        today,
                        filterData
                          .find((i) => i.key === filterButtonKey)
                          .date.format("YYYY-MM-DD")
                      )}`
                }
              />
            )}
          </TabPane>
        )}
        {(!showOnlyPane || showOnlyPane === "2") && (
          <TabPane tab={"Weld Inches"} key="2" style={{ height: 407 }}>
            {weldInchesSum || s3WeldInchesSum ? (
              <Line {...weldInchesConfig} chartRef={ref} />
            ) : (
              <Empty
                description={
                  weldInchesSum || s3WeldInchesSum
                    ? `No Weld Data`
                    : `No Weld Data Since ${dataOffsetDate(
                        date,
                        today,
                        filterData
                          .find((i) => i.key === filterButtonKey)
                          .date.format("YYYY-MM-DD")
                      )}`
                }
              />
            )}
          </TabPane>
        )}
        {(!showOnlyPane || showOnlyPane === "3") && (
          <TabPane tab={"Operator"} key="3" style={{ height: 407 }}>
            <>
              <Row>
                <Col span={24} style={{ paddingBottom: 20 }}>
                  <>
                    <b>Top Operators by Weld Hours</b>
                    <div>
                      {!isEmpty(operatorsWeldHours) ? (
                        <Bar {...operatorsWeldHoursConfig} chartRef={ref} />
                      ) : (
                        <Empty
                          description={
                            weldHoursSum || s3WeldHoursSum
                              ? `No Weld Data`
                              : `No Weld Data Since ${dataOffsetDate(
                                  date,
                                  today,
                                  filterData
                                    .find((i) => i.key === filterButtonKey)
                                    .date.format("YYYY-MM-DD")
                                )}`
                          }
                        />
                      )}
                    </div>
                  </>
                </Col>
                <Col span={24}>
                  <>
                    <b>Top Operators by Weld Inches (FDI)</b>
                    <div>
                      {!isEmpty(operatorsWeldInches) ? (
                        <Bar {...operatorsWeldInchesConfig} chartRef={ref} />
                      ) : (
                        <Empty
                          description={
                            weldInchesSum || s3WeldInchesSum
                              ? `No Weld Data`
                              : `No Weld Data Since ${dataOffsetDate(
                                  date,
                                  today,
                                  filterData
                                    .find((i) => i.key === filterButtonKey)
                                    .date.format("YYYY-MM-DD")
                                )}`
                          }
                        />
                      )}
                    </div>
                  </>
                </Col>
              </Row>
            </>
          </TabPane>
        )}
      </Tabs>
    </>
  );
};

export default RobotPerformance;
